import cn from 'classnames';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  ButtonUI,
  Checkbox,
  EmptyState,
  Loader,
  RadioButtonsGroup,
  Selector,
  Switch,
  Toast
} from 'sonata-react-components';

import { FileUploader } from '../MultiFlightMap/components/AttributionPOI/FileUploader/FileUploader';
import { DELIMITER_CHARS, normalizePreview, removeEmptyRows } from './csvImporter.helpers';
import { getImportErrorsToastText } from './ImportErrors';

import type { SelectorOptionPropsInterface } from 'sonata-react-components';
import type { DataProcessResult } from '@/services/file.service';
import type { CsvImporterColumn, CsvImporterColumnGroup, CsvImporterConfig, CsvMapping } from './csvImporter.helpers';

import './csvImporter.styles.scss';

type NoticeType = 'error' | 'info' | 'success';

type Notice = {
  secondaryText?: string;
  text: string;
  type: NoticeType;
};

const NOTICE_ICONS: Record<NoticeType, string> = {
  error: 'fa-light fa-circle-x',
  info: 'fa-light fa-info-circle',
  success: 'fa-light fa-circle-check'
};

const NOTICE_TOAST_TYPES: Record<NoticeType, 'danger' | 'info' | 'success'> = {
  error: 'danger',
  info: 'info',
  success: 'success'
};

type Props = {
  applyText: string;
  columnGroups: CsvImporterColumnGroup[];
  config: CsvImporterConfig;
  country?: {
    onChange: (option: SelectorOptionPropsInterface) => void;
    options: SelectorOptionPropsInterface[];
    value: SelectorOptionPropsInterface[];
  };
  file: File;
  idPrefix: string;
  isApplyEnabled: boolean;
  isApplying: boolean;
  isPreprocessing: boolean;
  mapping: CsvMapping[];
  notice?: Notice;
  onApply: () => void;
  onConfigChange: (config: CsvImporterConfig) => void;
  /** Enables the "Remove empty rows" checkbox, which swaps the file for a copy without its empty rows and back */
  onFileChange?: (file: File) => void;
  onGoBack: () => void;
  onMappingChange: (mapping: CsvMapping[]) => void;
  preprocess?: DataProcessResult;
  subtitle: string;
  title: string;
};

export function CsvImporter({
  applyText,
  columnGroups,
  config,
  country,
  file,
  idPrefix,
  isApplyEnabled,
  isApplying,
  isPreprocessing,
  mapping,
  notice,
  onApply,
  onConfigChange,
  onFileChange,
  onGoBack,
  onMappingChange,
  preprocess,
  subtitle,
  title
}: Props) {
  const { formatMessage } = useIntl();
  // The file as uploaded while its empty rows are removed, to restore it when the checkbox is unchecked
  const [emptyRowsRemoval, setEmptyRowsRemoval] = useState<{ originalFile: File; removed: number }>();

  const { hasDelimiterError, normalizedDataPreview, normalizedHeaders } = useMemo(
    () => normalizePreview(preprocess),
    [preprocess]
  );

  const delimiters = [
    {
      id: 'semicolon',
      itemClassName: 'csvImporter__configFormFieldGroupRadioButtonsItem',
      text: formatMessage({ defaultMessage: 'Semicolon', id: 'poiImporter.delimiter.semicolon' })
    },
    {
      id: 'comma',
      itemClassName: 'csvImporter__configFormFieldGroupRadioButtonsItem',
      text: formatMessage({ defaultMessage: 'Comma', id: 'poiImporter.delimiter.comma' })
    },
    {
      id: 'space',
      itemClassName: 'csvImporter__configFormFieldGroupRadioButtonsItem',
      text: formatMessage({ defaultMessage: 'Space', id: 'poiImporter.delimiter.space' })
    },
    {
      id: 'tab',
      itemClassName: 'csvImporter__configFormFieldGroupRadioButtonsItem',
      text: formatMessage({ defaultMessage: 'Tab', id: 'poiImporter.delimiter.tab' })
    }
  ];

  const separators = [
    {
      id: 'comma',
      itemClassName: 'csvImporter__configFormFieldGroupRadioButtonsItem',
      text: formatMessage({ defaultMessage: 'Comma (Ex.: 1.9912,1)', id: 'poiImporter.separator.comma' })
    },
    {
      id: 'dot',
      itemClassName: 'csvImporter__configFormFieldGroupRadioButtonsItem',
      text: formatMessage({ defaultMessage: 'Dot (Ex.: 1,9912.1)', id: 'poiImporter.separators.dot' })
    }
  ];

  function handleDelimiterChange(delimiter: string) {
    onConfigChange({ ...config, delimiter: delimiter as CsvImporterConfig['delimiter'] });
  }

  async function handleRemoveEmptyRowsChange(checked: boolean) {
    if (!checked) {
      onFileChange?.(emptyRowsRemoval!.originalFile);
      setEmptyRowsRemoval(undefined);
      return;
    }
    const { file: cleanedFile, removed } = await removeEmptyRows(file, DELIMITER_CHARS[config.delimiter]);
    setEmptyRowsRemoval({ originalFile: file, removed });
    onFileChange?.(cleanedFile);
  }

  const hasOnlyEmptyRowErrors =
    preprocess?.status === 'error' && preprocess.errors.every(({ type }) => type === 'Empty row');
  const showRemoveEmptyRows = !!onFileChange && (!!emptyRowsRemoval || hasOnlyEmptyRowErrors);

  function handleSeparatorChange(separator: string) {
    onConfigChange({ ...config, separator: separator as CsvImporterConfig['separator'] });
  }

  function handleColumnChange(fileCol: string | undefined, key: string) {
    const next = mapping.filter(entry => entry.key !== key);
    if (fileCol) {
      next.push({ fileCol, key });
    }
    onMappingChange(next);
  }

  function getColumnSelector(column: CsvImporterColumn) {
    const currentFileCol = mapping.find(entry => entry.key === column.key)?.fileCol;
    const colsUsedElsewhere = mapping.filter(entry => entry.key !== column.key).map(entry => entry.fileCol);

    const availableOptions = (preprocess?.headers || [])
      .filter(header => !colsUsedElsewhere.includes(header))
      .map(header => ({ id: header, label: header }))
      .sort((a, b) => a.label.localeCompare(b.label));

    return (
      <Selector
        className={cn('columnSelector', { 'columnSelector--fullWidth': column.fullWidth })}
        id={`${idPrefix}-column-mapping-${column.key}`}
        isClearable
        isDisabled={isPreprocessing || hasDelimiterError}
        key={column.key}
        label={column.label}
        onChange={(selected: SelectorOptionPropsInterface) =>
          handleColumnChange(selected.id as string | undefined, column.key)
        }
        options={availableOptions}
        placeholder={formatMessage({
          defaultMessage: 'Select a header from file',
          id: 'poiImporter.columnSelector.placeholder'
        })}
        required={column.required}
        {...(column.tooltipText ? { tooltipText: column.tooltipText } : {})}
        tiny
        type="simple"
        value={currentFileCol ? [{ id: currentFileCol, label: currentFileCol }] : []}
      />
    );
  }

  const previewStatusToast = (() => {
    if (notice) {
      return {
        icon: NOTICE_ICONS[notice.type],
        secondaryText: notice.secondaryText,
        text: notice.text,
        type: NOTICE_TOAST_TYPES[notice.type]
      };
    }

    if (preprocess?.status === 'error') {
      const { secondaryText, text } = getImportErrorsToastText(preprocess, formatMessage);
      return {
        icon: NOTICE_ICONS.error,
        secondaryText,
        text,
        type: NOTICE_TOAST_TYPES.error
      };
    }

    if (preprocess?.status === 'success' && isApplyEnabled) {
      return {
        icon: NOTICE_ICONS.success,
        secondaryText: emptyRowsRemoval
          ? formatMessage(
              {
                defaultMessage:
                  '{removed, plural, one {# empty row was} other {# empty rows were}} removed from your file.',
                id: 'poiImporter.status.success.emptyRowsRemoved'
              },
              { removed: emptyRowsRemoval.removed }
            )
          : undefined,
        text: formatMessage({
          defaultMessage: 'Your file data configuration is correct',
          id: 'poiImporter.status.success'
        }),
        type: NOTICE_TOAST_TYPES.success
      };
    }

    return null;
  })();

  return (
    <main className="csvImporter">
      <section className="csvImporter__config">
        <header className="csvImporter__configHeader">
          <div className="csvImporter__file">
            <div className="csvImporter__fileButton">
              <ButtonUI
                icon="fa-light fa-arrow-left"
                id={`${idPrefix}_fileBack`}
                onClick={onGoBack}
                shape="link"
                size="ultraSmall"
                type="primary"
              />
            </div>
            <FileUploader file={file} />
          </div>
        </header>
        <article className="csvImporter__configForm">
          <header className="csvImporter__configFormHeader">
            <h2 className="csvImporter__configFormHeaderTitle">{title}</h2>
            <p className="csvImporter__configFormHeaderSubtitle">{subtitle}</p>
          </header>
          <div className="csvImporter__configFormFieldGroup">
            {country && (
              <div className="csvImporter__configFormFieldGroupSelector">
                <Selector
                  id={`${idPrefix}_countrySelector`}
                  label={formatMessage({ defaultMessage: 'Country', id: 'entityFileUpload.' })}
                  onChange={country.onChange}
                  options={country.options}
                  placeholder={formatMessage({
                    defaultMessage: 'Select a country...',
                    id: 'poiImporter.config.country.placeholder'
                  })}
                  required
                  tiny
                  tooltipText={formatMessage({
                    defaultMessage: 'Indicate the country of the venues, only one country of origin is supported.',
                    id: 'entityFileUpload.country'
                  })}
                  type="simple"
                  value={country.value}
                />
              </div>
            )}
            <label className="csvImporter__configFormFieldGroupLabel">
              {formatMessage({ defaultMessage: 'Column Delimiter', id: 'poiImporter.config.delimiter.label' })}
            </label>
            <RadioButtonsGroup
              className="csvImporter__configFormFieldGroupRadioButtons"
              id={`${idPrefix}_delimiters`}
              items={delimiters}
              onChange={handleDelimiterChange}
              tiny
              value={config.delimiter}
            />
            <label className="csvImporter__configFormFieldGroupLabel">
              {formatMessage({ defaultMessage: 'Decimal Separator', id: 'poiImporter.config.separator.label' })}
            </label>
            <RadioButtonsGroup
              className="csvImporter__configFormFieldGroupRadioButtons"
              id={`${idPrefix}_separators`}
              items={separators}
              onChange={handleSeparatorChange}
              tiny
              value={config.separator}
            />
          </div>
          {columnGroups.map(group => (
            <div className="csvImporter__configFormFieldGroup" key={group.key}>
              {group.label && (
                <div className="csvImporter__configFormFieldGroupLabelWrapper">
                  <label className="csvImporter__configFormFieldGroupLabel">{group.label}</label>
                  {group.description && (
                    <label className="csvImporter__configFormFieldGroupLabelDescription">{group.description}</label>
                  )}
                </div>
              )}
              {group.notice && (
                <Toast
                  autoClose={false}
                  closeButton={false}
                  icon={NOTICE_ICONS.info}
                  id={`${idPrefix}_${group.key}Notice`}
                  secondaryText={group.notice.secondaryText}
                  text={group.notice.text}
                  type="default"
                />
              )}
              <div className="csvImporter__configFormGrid">
                {group.columns.map(column => getColumnSelector(column))}
              </div>
            </div>
          ))}
          <footer className="csvImporter__actions">
            {showRemoveEmptyRows && (
              <Checkbox
                active={!!emptyRowsRemoval}
                id={`${idPrefix}_removeEmptyRows`}
                labelText={formatMessage({
                  defaultMessage: 'Remove the empty rows from my file',
                  id: 'poiImporter.config.removeEmptyRows'
                })}
                onClickAction={(_, checked) => handleRemoveEmptyRowsChange(checked)}
                type="checkbox"
              />
            )}
            <div className="csvImporter__actionsButtons">
              <ButtonUI
                icon="fa-light fa-arrow-left"
                id={`${idPrefix}_cancel`}
                onClick={onGoBack}
                size="medium"
                text={formatMessage({ defaultMessage: 'Back', id: 'poiImporter.button.cancel' })}
                type="secondary"
              />
              <ButtonUI
                disabled={!isApplyEnabled}
                id={`${idPrefix}_send`}
                loading={isApplying}
                onClick={onApply}
                size="medium"
                text={applyText}
                type="primary"
              />
            </div>
          </footer>
        </article>
      </section>
      <section className="csvImporter__preview">
        <h2 className="csvImporter__previewTitle">
          {formatMessage({ defaultMessage: "This is your document's preview", id: 'poiImporter.preview.title' })}
        </h2>
        <div className="csvImporter__previewContent">
          {previewStatusToast && (
            <div className="csvImporter__previewStatus">
              <Toast
                autoClose={false}
                closeButton={false}
                icon={previewStatusToast.icon}
                id={`${idPrefix}_previewStatus`}
                secondaryText={previewStatusToast.secondaryText}
                text={previewStatusToast.text}
                type={previewStatusToast.type}
              />
            </div>
          )}
          <Switch>
            <Switch.Case condition={isPreprocessing}>
              <Loader
                className="csvImporter__configSpinner"
                id={`${idPrefix}_loading`}
                type="landing"
                variant="default"
              >
                <Loader.Title className="csvImporter__configSpinnerTitle">
                  {formatMessage({
                    defaultMessage: 'Please wait, this may take a few seconds...',
                    id: 'poiImporter.status.loading'
                  })}
                </Loader.Title>
              </Loader>
            </Switch.Case>
            <Switch.Case condition={!isPreprocessing && normalizedDataPreview.length > 0}>
              <div className="csvImporter__previewContentTableWrapper">
                <table className="csvImporter__previewContentTable">
                  <thead>
                    <tr>
                      <th className="csvImporter__previewContentTableHead">
                        {formatMessage({ defaultMessage: 'Row', id: 'poiImporter.preview.table.row' })}
                      </th>
                      {normalizedHeaders.map((header, headerIndex) => (
                        <th
                          className="csvImporter__previewContentTableHead"
                          key={`${header}-${headerIndex}`}
                          title={header}
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {normalizedDataPreview.map((row, rowIndex) => {
                      const hasRowError = preprocess?.errors?.some(error => error.row - 1 === rowIndex);

                      return (
                        <tr key={rowIndex}>
                          <td
                            className={cn('csvImporter__previewContentTableCell', {
                              'csvImporter__previewContentTableCell--error': hasRowError && !hasDelimiterError
                            })}
                          >
                            {rowIndex + 1}
                          </td>
                          {row.map((cell, cellIndex) => (
                            <td
                              className={cn('csvImporter__previewContentTableCell', {
                                'csvImporter__previewContentTableCell--columnError':
                                  preprocess?.errors?.some(
                                    error => error.row - 1 === rowIndex && error.column === cellIndex
                                  ) && !hasDelimiterError,
                                'csvImporter__previewContentTableCell--error': hasRowError && !hasDelimiterError
                              })}
                              key={cellIndex}
                              title={cell}
                            >
                              {cell || '-'}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Switch.Case>
            <Switch.Default>
              <div className="csvImporter__emptyState">
                <EmptyState
                  description={formatMessage({
                    defaultMessage: 'Complete your details on the left to see your preview',
                    id: 'poiImporter.empty.description'
                  })}
                  id={`${idPrefix}-emptyState`}
                  show
                  title={formatMessage({ defaultMessage: 'Nothing to see yet!', id: 'poiImporter.empty.title' })}
                />
              </div>
            </Switch.Default>
          </Switch>
        </div>
      </section>
    </main>
  );
}
